import React from 'react';
import {
  DASHBOARD_QUICK_AMOUNTS,
  DASHBOARD_QUICK_UNITS,
  DASHBOARD_REFRESH_UNITS,
  DASHBOARD_TIME_PRESETS,
  resolveDashboardTimeWindow
} from '../utils/dashboardTimeRange';
import { useUserTimezone } from '../hooks/useUserTimezone';
import './DashboardTimeRangeControls.css';

export default function DashboardTimeRangeControls({ selection, onChange }) {
  const { timezone } = useUserTimezone();
  const mode = selection?.timeMode || 'absolute';
  const windowRange = mode === 'absolute' ? null : resolveDashboardTimeWindow(selection, timezone);

  return (
    <div className="dashboard-time-controls">
      <label className="time-range-label" htmlFor="dashboard-time-mode">Time range</label>
      <select
        id="dashboard-time-mode"
        className="sidebar-input"
        value={mode}
        onChange={(e) => onChange({ timeMode: e.target.value })}
      >
        <option value="preset">Presets</option>
        <option value="quick">Quick select</option>
        <option value="absolute">Date &amp; time range</option>
      </select>

      {mode === 'preset' && (
        <>
          <label className="time-range-label" htmlFor="dashboard-time-preset">Preset</label>
          <select
            id="dashboard-time-preset"
            className="sidebar-input"
            value={selection.timePreset || '15m'}
            onChange={(e) => onChange({ timePreset: e.target.value })}
          >
            {DASHBOARD_TIME_PRESETS.map((preset) => (
              <option key={preset.id} value={preset.id}>{preset.label}</option>
            ))}
          </select>
        </>
      )}

      {mode === 'quick' && (
        <>
          <label className="time-range-label">Last</label>
          <div className="dashboard-time-pair">
            <select
              className="sidebar-input"
              aria-label="Quick select amount"
              value={String(selection.quickAmount || 15)}
              onChange={(e) => onChange({ quickAmount: Number(e.target.value) })}
            >
              {DASHBOARD_QUICK_AMOUNTS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <select
              className="sidebar-input"
              aria-label="Quick select unit"
              value={selection.quickUnit || 'minutes'}
              onChange={(e) => onChange({ quickUnit: e.target.value })}
            >
              {DASHBOARD_QUICK_UNITS.map((unit) => (
                <option key={unit.id} value={unit.id}>{unit.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {mode === 'absolute' && (
        <>
          <label className="time-range-label" htmlFor="dashboard-time-start">Start time</label>
          <input
            id="dashboard-time-start"
            type="datetime-local"
            step={0.001}
            className="sidebar-input time-input"
            value={selection.startTime || ''}
            onChange={(e) => onChange({ startTime: e.target.value })}
          />
          <label className="time-range-label" htmlFor="dashboard-time-end">End time</label>
          <input
            id="dashboard-time-end"
            type="datetime-local"
            step={0.001}
            className="sidebar-input time-input"
            value={selection.endTime || ''}
            onChange={(e) => onChange({ endTime: e.target.value })}
          />
        </>
      )}

      {windowRange && (
        <p className="dashboard-time-hint">
          {windowRange.startTime && windowRange.endTime
            ? `${windowRange.startTime.replace('T', ' ')} → ${windowRange.endTime.replace('T', ' ')}`
            : 'Uses your selected timezone (Settings).'}
        </p>
      )}
      {mode === 'absolute' && (
        <p className="dashboard-time-hint">Uses your selected timezone (Settings).</p>
      )}

      <label className="time-range-label">Refresh every</label>
      <div className="dashboard-time-pair">
        <select
          className="sidebar-input"
          aria-label="Refresh interval amount"
          value={selection.refreshAmount === '' || selection.refreshAmount == null ? '' : String(selection.refreshAmount)}
          onChange={(e) => onChange({ refreshAmount: e.target.value === '' ? '' : Number(e.target.value) })}
        >
          <option value="">Off</option>
          {DASHBOARD_QUICK_AMOUNTS.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <select
          className="sidebar-input"
          aria-label="Refresh interval unit"
          value={selection.refreshUnit || 'minutes'}
          disabled={!selection.refreshAmount}
          onChange={(e) => onChange({ refreshUnit: e.target.value })}
        >
          {DASHBOARD_REFRESH_UNITS.map((unit) => (
            <option key={unit.id} value={unit.id}>{unit.label}</option>
          ))}
        </select>
      </div>
      <p className="dashboard-time-hint">Refreshes widgets. Presets and quick select move forward on each refresh.</p>
    </div>
  );
}
